/**
 * @file Hook: useSound
 * @description Custom hook that wraps the use-sound library and integrates with our AudioContext
 */

import type { Howl } from 'howler';
import { useSound as useOriginalSound } from 'use-sound';
import { useAudioContext } from '../contexts/AudioContext';

// Define types for use-sound since we might not have access to the library's types yet
interface HookOptions {
  volume?: number;
  playbackRate?: number;
  interrupt?: boolean;
  soundEnabled?: boolean;
  sprite?: Record<string, [number, number]>;
  // Add any other options that use-sound supports
  // biome-ignore lint/suspicious/noExplicitAny: Sound library types not fully typed
  [key: string]: any;
}

// ReturnedValue is a tuple with play function and exposed data
type ReturnedValue = [
  // Play function
  (options?: {
    id?: string;
    forceSoundEnabled?: boolean;
    playbackRate?: number;
  }) => void,
  // Exposed data
  {
    sound: Howl | null; // This would be Howl from howler.js when the library is installed
    stop: (id?: string) => void;
    pause: (id?: string) => void;
    duration: number | null;
  },
];

/**
 * Custom hook that wraps the use-sound library and integrates with our AudioContext
 * @param url The URL of the sound file to play
 * @param options Options for the sound, including category (MUSIC or SFX)
 * @returns The play function and additional controls from use-sound
 */
// biome-ignore lint/suspicious/noExplicitAny: Sound library types not fully typed
export function useSound<_T = any>(
  url: string,
  options: HookOptions & { category?: 'MUSIC' | 'SFX' } = { category: 'SFX' },
): ReturnedValue {
  const { isMusicEnabled, isSfxEnabled, musicVolume, sfxVolume } =
    useAudioContext();

  const { category = 'SFX', ...restOptions } = options;

  // Determine if sound should be enabled based on category
  const soundEnabled = category === 'MUSIC' ? isMusicEnabled : isSfxEnabled;

  // Set volume based on category
  const volume = category === 'MUSIC' ? musicVolume : sfxVolume;

  return useOriginalSound(url, {
    ...restOptions,
    soundEnabled,
    volume,
  });
}
